import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowSeparatorLight from "@/assets/icons/arrowSeparatorLight.svg?react";
import { useTags } from "@/hooks/useTags";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { AttributeType } from "@/models/attribute";
import { AttributeFilters } from "@/models/attributeFilter";

interface FilterInfoProps {
  categories?: string[];
  vocPeriodStart?: Date;
  vocPeriodEnd?: Date;
  attributeFilters?: AttributeFilters;
  tagIds?: string[];
}

const formatDate = (date: Date | undefined) => {
  if (!date) return null;
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(date));
};

const formatAttributeFilterValue = (filter: AttributeFilters[string]): string => {
  if (filter.type === AttributeType.CATEGORICAL) {
    return filter.values.join(" / ");
  } else if (filter.type === AttributeType.NUMERICAL) {
    const min = filter.gte ?? "";
    const max = filter.lte ?? "";
    return `${min}〜${max}`;
  } else if (filter.type === AttributeType.TEXTUAL) {
    return filter.value;
  }
  return "";
};

export const FilterInfo: React.FC<FilterInfoProps> = ({
  categories,
  vocPeriodStart,
  vocPeriodEnd,
  attributeFilters,
  tagIds,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation("voiceek_ui");
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { tags } = useTags(sessionCurrentWorkspaceId || "");

  const startStr = formatDate(vocPeriodStart);
  const endStr = formatDate(vocPeriodEnd);
  const periodStr = startStr && endStr ? `${startStr} 〜 ${endStr}` : t("chat.filter.analysisPeriod.all");

  type CategoryGroup = { parentCategory: string; categories: string[]; };

  const groupCategoriesByParent = (categories: string[]): CategoryGroup[] => {
    const map = new Map<string, string[]>();

    for (const value of categories) {
      const parts = value.split("/").filter(Boolean);
      const parent = parts.length > 1 ? parts.slice(0, -1).join("/") + "/" : "";

      const list = map.get(parent) ?? [];
      list.push(value);
      map.set(parent, list);
    }

    return Array.from(map, ([parentCategory, categories]) => ({
      parentCategory,
      categories,
    }));
  };

  const groupedCategories = categories ? groupCategoriesByParent(categories) : [];
  const attributeFilterEntries = attributeFilters ? Object.entries(attributeFilters) : [];

  return (
    <div className="flex flex-col">
      {/* トグルボタン */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-[#BECFFF]" />
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-[13px] text-[#6888e1] font-medium hover:text-[#4a6ef2] hover:cursor-pointer"
        >
          {t("chat.filter.showConditions")}
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-[-180deg]" : ""}`}
          />
        </button>
        <div className="flex-1 h-px bg-[#BECFFF]" />
      </div>

      {/* 条件表示部分 */}
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <table className="mt-2">
            <tbody className="[&>tr>td]:pt-1.5 [&>tr>th]:pt-1.5">
              {/* VOC対象期間 */}
              <tr className="">
                <th className="text-[13px] text-[#6888e1] font-medium text-left whitespace-nowrap">
                  {t("chat.filter.vocTargetPeriod")}
                </th>
                <td className="text-[13px] text-[#6888e1] font-medium px-1">
                  ：
                </td>
                <td className="text-[13px] text-[#6888e1] font-medium">
                  {periodStr}
                </td>
              </tr>

              {/* カテゴリー表示 */}
              {groupedCategories.length > 0
                && groupedCategories.map((grouped) => (
                  <tr key={grouped.parentCategory}>
                    <th className="text-[13px] text-[#6888e1] font-medium text-left whitespace-nowrap">
                      {t("chat.filter.category")}
                    </th>
                    <td className="text-[13px] text-[#6888e1] font-medium px-1">
                      ：
                    </td>
                    <td className="text-[13px] text-[#6888e1] font-medium">
                      <GroupedCategory
                        parentCategory={grouped.parentCategory}
                        categories={grouped.categories}
                      />
                    </td>
                  </tr>
                ))}

              {/* 属性フィルター表示 */}
              {attributeFilterEntries.length > 0
                && attributeFilterEntries.map(([attributeName, filter]) => (
                  <tr key={attributeName}>
                    <th className="text-[13px] text-[#6888e1] text-left whitespace-nowrap w-0 font-medium">
                      {attributeName}
                    </th>
                    <td className="text-[13px] text-[#6888e1] font-medium px-1">
                      ：
                    </td>
                    <td className="text-[13px] text-[#6888e1] font-medium">
                      {formatAttributeFilterValue(filter)}
                    </td>
                  </tr>
                ))}

              {/* タグ表示 */}
              {tagIds && tagIds.length > 0 && (
                <tr>
                  <th className="text-[13px] text-[#6888e1] text-left whitespace-nowrap w-0 font-medium align-top">
                    タグ
                  </th>
                  <td className="text-[13px] text-[#6888e1] font-medium px-1 align-top">
                    ：
                  </td>
                  <td className="text-[13px] text-[#6888e1] font-medium">
                    <div className="flex gap-2 flex-wrap">
                      {tags?.filter((t) => tagIds.includes(t.id)).map((tag) => (
                        <span
                          key={tag.id}
                          className="border border-[#6888e1] px-[10px] py-[3px] rounded-[13px] text-[12px] leading-[14px]"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

type GroupedCategoryProps = {
  parentCategory: string;
  categories: string[];
};

const GroupedCategory: React.FC<GroupedCategoryProps> = ({
  parentCategory,
  categories,
}) => {
  const splitedParentCategory = parentCategory.split("/").filter(Boolean);

  return (
    <div className="text-start leading-6 align-middle w-fit">
      <div
        className={cn(
          "inline w-fit items-center text-[12px]/[14px] py-1 px-[5px] rounded-[5px] hover:cursor-default bg-white",
        )}
      >
        {splitedParentCategory.map((part, i) => (
          <React.Fragment key={part}>
            {i > 0 && <ArrowSeparatorLight className="inline mt-[-1px]" color="white" />}
            <span className="inline whitespace-pre-line">
              <span className="text-[#474747] align-middle px-1">{part}</span>
            </span>
          </React.Fragment>
        ))}
        {splitedParentCategory.length > 0 && categories.length > 0 && (
          <ArrowSeparatorLight className="inline mt-[-1px]" color="white" />
        )}
        {categories.map((category, i) => {
          const last = category.split("/").findLast(Boolean) ?? "";
          return (
            <React.Fragment key={category}>
              {i > 0 && <span className="text-[#B6B6B6] w-[5px] text-center">/</span>}
              <span className="align-middle text-[#6888e1] px-1">{last}</span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
